import { Badge, Button, Checkbox } from '@lognai/synapse-design-system-v2';
import { ArrowUpRight, CalendarDays, Filter } from '@lognai/synapse-design-system-v2/icons';
import { AskLognAIIcon } from '@lognai/plugin-ai-core-ui';
import type { DashboardDatePreset } from '@lognai/plugin-analytics';
import type { ActiveInsight, AnalysisResult } from './api';
import { useChartInsightRefresh } from './useChartInsightRefresh';
import { formatInsightDate, compactInsightDates } from './insight-dates';
import { PersistentInsightPanel } from './PersistentInsightPanel';
import { formatEvidenceValue } from './insight-evidence';
import { chartInsightSummary, currentChartInsight } from './chart-insight-summary';

export interface ChartInsightContext {
  dashboardId: string;
  tabId?: string;
  widgetId: string;
  datePreset: DashboardDatePreset;
  visualVersion: number;
  insights: ActiveInsight[];
  persistedEnabled: boolean;
  canPersist: boolean;
  enabled: boolean;
  zeroIsPositive?: boolean;
  onRefresh: () => Promise<void>;
  onEnabledChange?: (enabled: boolean) => void;
}

export function ChartInsightWorkspace({ context, result, onAsk }: {
  context: ChartInsightContext; result?: AnalysisResult; onAsk: (question: string) => void;
}) {
  const autoRefresh = useChartInsightRefresh({ dashboardId: context.dashboardId, tabId: context.tabId,
    widgetId: context.widgetId, datePreset: context.datePreset, visualVersion: context.visualVersion,
    result, insights: context.insights, enabled: context.enabled && context.persistedEnabled,
    canPersist: context.canPersist, onRefresh: context.onRefresh,
  });
  const explanations = context.insights.filter(item => item.revision.source !== 'manual');
  const primary = explanations.find(item => result && currentChartInsight(result, item)) ?? explanations[0];
  const notes = context.insights.filter(item => item.revision.source === 'manual');
  const current = Boolean(result && currentChartInsight(result, primary));
  const summary = chartInsightSummary(result, primary, context.zeroIsPositive);
  const suggestions = current && primary?.revision.suggestedQuestions.length
    ? primary.revision.suggestedQuestions.slice(0, 3)
    : ['What is driving these results?', result?.analysis.timeDimension ? 'What changed across the visible period?' : 'Which groups contribute the most?', 'What should I investigate next?'];
  const evidence = result?.meta?.rowState === 'empty' ? [] : (result?.evidence ?? []).filter(fact => typeof fact.value !== 'number' || Number.isFinite(fact.value)).slice(0, 4);
  const range = result?.analysis.timeDimension?.dateRange;
  const filterCount = result?.analysis.filters.length ?? 0;
  const panelProps = {
    dashboardId: context.dashboardId, tabId: context.tabId, widgetId: context.widgetId,
    datePreset: context.datePreset, visualVersion: context.visualVersion, analysis: result?.analysis,
    filters: result?.analysis.filters ?? [], canPersist: context.canPersist,
    onRefresh: context.onRefresh, managementOnly: true, onSuggestedQuestion: onAsk, mode: 'open' as const,
  };

  return <div className="chart-insight-workspace">
    <section className="chart-insight-takeaway" aria-label="Current chart takeaway">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="chart-insight-brand-icon" aria-hidden="true"><AskLognAIIcon size={22} /></span>
          <div><h3 className="text-sm font-semibold text-foreground">At a glance</h3><p className="mt-0.5 text-xs text-muted-foreground">{summary.source === 'ai' ? 'AI insight' : 'Calculated from chart data'}</p></div>
        </div>
        <Badge variant="outline">{autoRefresh.refreshing ? 'Updating insight' : 'Current view'}</Badge>
      </div>
      <p className="mt-5 text-base font-medium leading-7 text-foreground">{summary.text}</p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
        {range && <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} aria-hidden="true" />{range.map(date => formatInsightDate(date)).join(' – ')}</span>}
        <span className="inline-flex items-center gap-1.5"><Filter size={13} aria-hidden="true" />{filterCount ? `${filterCount} active filter${filterCount === 1 ? '' : 's'}` : 'No chart filters'}</span>
        <span className="ml-auto">{context.enabled ? 'Shown below chart' : 'Hidden on chart'}</span>
      </div>
      {context.onEnabledChange && <label className="mt-4 flex cursor-pointer items-center gap-2 border-t border-border pt-3 text-sm"><Checkbox checked={context.enabled} onCheckedChange={checked => context.onEnabledChange?.(checked === true)} />Show insight below this chart</label>}
    </section>

    <section aria-label="Explore this chart" className="grid gap-3">
      <div><h3 className="text-sm font-semibold">Explore this chart</h3><p className="mt-1 text-xs text-muted-foreground">Continue in Assist with the current chart and filters.</p></div>
      <div className="chart-insight-prompts">{suggestions.map(question => <Button key={question} variant="outline" className="chart-insight-prompt" disabled={!result} onClick={() => onAsk(question)}><span className="min-w-0 flex-1 whitespace-normal text-left text-xs leading-5">{question}</span><ArrowUpRight size={14} className="shrink-0 text-primary" /></Button>)}</div>
    </section>

    <div className="chart-insight-columns">
      <div className="grid min-w-0 content-start gap-4">
        {context.persistedEnabled && <PersistentInsightPanel {...panelProps} autoRefreshStatus={autoRefresh} showAddNote={false} active={primary ? { ...primary, stale: !current } : undefined} />}
        <section className="chart-insight-evidence" aria-label="Supporting figures">
          <div className="border-b border-border px-4 py-3"><h3 className="text-sm font-semibold">Supporting figures</h3><p className="mt-1 text-xs text-muted-foreground">From the current chart data.</p></div>
          {evidence.length ? <dl className="chart-insight-facts">{evidence.map(fact => <div key={fact.id} className="min-w-0 p-4"><dt className="text-xs leading-5 text-muted-foreground">{compactInsightDates(fact.label)}</dt><dd className="mt-1 break-words text-lg font-semibold tabular-nums text-foreground">{formatEvidenceValue(fact)}</dd>{fact.context && <p className="mt-1 text-xs leading-5 text-muted-foreground">{compactInsightDates(fact.context)}</p>}</div>)}</dl>
            : <p className="px-4 py-5 text-xs leading-5 text-muted-foreground">{result ? 'No aggregate figures are available for this view. Check the Data tab for the plotted values.' : 'Supporting figures will appear when the chart loads.'}</p>}
        </section>
      </div>
      {context.persistedEnabled && <section className="grid min-w-0 content-start gap-3" aria-label="Chart team notes">
        <PersistentInsightPanel {...panelProps} notesOnly noteCount={notes.length} />
        {notes.map(note => <PersistentInsightPanel key={note.thread.id} {...panelProps} notesOnly active={note} showAddNote={false} />)}
      </section>}
    </div>
  </div>;
}
